import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Spin } from 'antd';
import { DateTime } from 'luxon';
import { PropTypes } from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import Permissions from '../auth/Permissions';
import Analytics from '../helpers/Analytics';

import BreadCrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';

import FormDetailView from '../forms/CompletedForms/FormDetailView';

import ScheduleHeader from './ScheduleHeader';
import DailySchedule from './DailySchedule/DailySchedule';
import WeeklySchedule from './WeeklySchedule/WeeklySchedule';
import MonthlySchedule from './MonthlySchedule/MonthlySchedule';
import ScheduleDrawer from './ScheduleDrawer';
import UserWeeklySchedule from './UserSchedule/UserWeeklySchedule';
import UserList from './UserSchedule/UserList';
import EquipmentWeeklySchedule from './EquipmentSchedule/EquipmentWeeklySchedule';
import EquipmentList from './EquipmentSchedule/EquipmentList';
import ScheduleDayList from './ScheduleDayList';
import RecurringChangeModal from '../common/modals/RecurringChangeModal';

import {
  filterShifts,
  getDayKey,
  getId,
  timestampToPosition,
  prepareShiftUpdatePayload,
  refineReminders,
  getWeekStart,
  getBiWeeklyEnd,
  getMaxStartFromRestriction,
  getWeekEnd,
  getDefaultShiftTimes,
  getViewDateRange,
  getScheduleAxis,
} from './scheduleHelpers';

import {
  getSchedule,
  createShift,
  updateShift,
  deleteShift,
  getUserFieldScheduleFilterViews,
  applyToShift,
  getScheduleCategories,
} from './state/schedule.actions';
import { getUserLabels, getUsers } from '../users/state/users.actions';
import {
  getFormById,
  getTemplates,
  getStatuses,
  getCustomTables,
} from '../forms/state/forms.actions';
import { getCustomerLabels, getCustomers } from '../contacts/customers/state/customers.actions';
import {
  getEmails,
} from '../emails/state/email.actions';
import { getBillingRates } from '../billingRates/state/billingRates.actions';

import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import { getProjects } from '../projects/state/projects.actions';
import { getDivisions } from '../settings/state/settings.actions';
import FilterDrawer from '../common/filters/FilterDrawer';
import FieldScheduleCustomFilterTab from './Filters/FieldScheduleCustomFilterTab';
import UnassignedScheduleSidebar from './UnassignedScheduleSidebar';
import { SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY } from './schedule.constants';
import { getEquipment, getEquipmentTypes } from '../equipment/state/equipment.actions';
import { getMaterials } from '../materials/state/materials.actions';

import useUpdateCostcodeIdMap from '../costcodes/useUpdateCostcodeIdMap';
import exportFieldScheduleToExcel from './fieldScheduleExport';

const getViewTypeFromAsync = () => {
  const type = window.localStorage.getItem('scheduleViewType') ?? 'Week';
  if (type === 'User' || type === 'user') {
    window.localStorage.setItem('scheduleViewType', 'UserDay');
    return 'UserDay';
  }
  return type;
};
const RESOURCE_AXIS_VIEWS = {
  user: { Grid: UserWeeklySchedule, List: UserList },
  equipment: { Grid: EquipmentWeeklySchedule, List: EquipmentList },
};

const crumbs = [
  { text: 'Scheduling', style: { color: 'lab(41.9742% -2.00994 -7.05371)' } },
  { text: 'Field Schedule', style: { color: 'lab(8.20799 -2.04457 -13.5596)' } },
];

const showDeleteModal = (shift, callback) => (
  CustomConfirmModal({
    title: `Delete ${shift.title}`,
    okText: 'Delete',
    cancelText: 'Cancel',
    onOk: callback,
  })
);

export default function Schedule({
  history,
}) {
  const defaultDisplay = !Permissions.has('SCHEDULE_WRITE');
  const dispatch = useDispatch();
  const stateShifts = useSelector((state) => state.schedule.shifts);
  const shiftMap = useSelector((state) => state.schedule.shiftMap);
  const filterViews = useSelector((state) => state.schedule.fieldScheduleFilterViews);
  const filters = useSelector((state) => state.schedule.fieldScheduleFilters);
  const divisions = useSelector((state) => state.settings.divisions);
  const companySettings = useSelector((state) => state.settings.company);
  const users = useSelector((state) => state.users.users);
  const projects = useSelector((state) => state.projects.projects);
  const phases = useSelector((state) => state.costcodes.phases);
  const teams = useSelector((state) => state.teams.teams);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const formTemplates = useSelector((state) => state.forms.templates);
  const scheduleRestriction = useSelector((state) => state.settings.scheduleRestriction);
  const equipment = useSelector((state) => state.equipment.equipment);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);

  const [viewType, setViewType] = useState(getViewTypeFromAsync());
  const [layout, setLayout] = useState('calendar');
  const [date, setDate] = useState(DateTime.local());
  const [showDrawer, setShowDrawer] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [filterDrawerTab, setFilterDrawerTab] = useState(null);
  const [selectedShift, setSelectedShift] = useState();
  const [newShift, setNewShift] = useState();
  const [editShift, setEditShift] = useState();
  const editShiftRef = useRef(editShift);
  editShiftRef.current = editShift;
  const [shifts, setShifts] = useState({});
  const [searchText, setSearchText] = useState();
  const [isDisplay, setIsDisplay] = useState(defaultDisplay);
  const [changeModalMode, setChangeModalMode] = useState();
  const [changeModalType, setChangeModalType] = useState();
  const [stagedUpdate, setStagedUpdate] = useState();
  const [showFormDetail, setShowFormDetail] = useState();
  const [lastDownload, setLastDownload] = useState();
  const [loading, setLoading] = useState(false);
  const [rangeStartDate, setRangeStartDate] = useState(DateTime.local());
  const [rangeEndDate, setRangeEndDate] = useState(DateTime.local());
  const [canUseHotkeys, setCanUseHotkeys] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const weeksRestriction = useMemo(() => {
    const ourUser = users.find((u) => u.id === Permissions.id);
    return scheduleRestriction[ourUser?.position]?.weeks ?? null;
  }, [users, scheduleRestriction]);

  useEffect(() => {
    const isSideBarOpen = window.localStorage.getItem(SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY) !== 'true';
    setIsSidebarOpen(isSideBarOpen);
  }, []);

  useEffect(() => {
    dispatch(getProjects());
    dispatch(getUsers());
    dispatch(getDivisions());
    dispatch(getUserFieldScheduleFilterViews());
    dispatch(getScheduleCategories());
    dispatch(getEquipment());
    dispatch(getEquipmentTypes());
    dispatch(getMaterials());
  }, []);

  const hideDrawer = useCallback(() => {
    setShowDrawer(false);
    setSelectedShift();
    setNewShift();
    setEditShift();
    setIsDisplay(defaultDisplay);
    setChangeModalMode();
    setChangeModalType();
    setStagedUpdate();
  }, []);

  const openFormDetail = useCallback(async (formId) => {
    if (await dispatch(getFormById(formId))) {
      setShowFormDetail(true);
    }
  }, []);

  const closeFormDetail = useCallback(() => {
    setShowFormDetail(false);
    if (editShift) {
      if (editShift.id in shiftMap) {
        // Force reload data
        setShowDrawer(false);
        setEditShift({
          ...editShift,
          ...shiftMap[editShift.id] ?? {},
        });
        setTimeout(() => {
          setShowDrawer(true);
        }, 200);
      } else {
        hideDrawer();
      }
    }
  }, [hideDrawer, editShift, shiftMap]);

  const hideChangeModal = useCallback(() => {
    setChangeModalMode();
    setChangeModalType();
  }, []);

  const onShiftCreate = useCallback((shift) => {
    const { day } = shift;
    setSelectedShift({
      ...shift,
      date: day.toMillis(),
    });
    setShowDrawer(true);
    setIsDisplay(false);
  }, []);

  const openDrawer = useCallback(() => {
    const defaults = getDefaultShiftTimes(companySettings?.settings, { baseDay: date });
    let shiftDay = date;
    let startTime = date.set({ hour: 9, minute: 0 }).toMillis();
    let endTime = date.set({ hour: 10, minute: 0 }).toMillis();

    if (defaults) {
      const {
        day: defaultDay,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      } = defaults;

      if (defaultDay) {
        shiftDay = defaultDay;
      }
      if (defaultStartTime && defaultEndTime) {
        startTime = defaultStartTime;
        endTime = defaultEndTime;
      } else if (defaultDay) {
        startTime = shiftDay.set({ hour: 9, minute: 0 }).toMillis();
        endTime = shiftDay.set({ hour: 10, minute: 0 }).toMillis();
      }
    }

    const dayStart = shiftDay.startOf('day');
    setSelectedShift();
    setEditShift();
    setNewShift({
      id: getId(),
      day: dayStart,
      date: dayStart.toMillis(),
      startTime,
      endTime,
      top: timestampToPosition(startTime, dayStart),
      bottom: timestampToPosition(endTime, dayStart),
    });
    setShowDrawer(true);
    setIsDisplay(false);
  }, [date, companySettings]);

  const closeFilterDrawer = () => {
    setCanUseHotkeys(true);
    setShowFilterDrawer(false);
    setFilterDrawerTab(null);
  };

  const openFilterDrawer = (tab) => {
    setFilterDrawerTab(tab || 'custom');
    setCanUseHotkeys(false);
    setShowFilterDrawer(true);
  };

  const onDateChange = useCallback((newDate) => {
    const ts = newDate.valueOf();
    const dt = DateTime.fromMillis(ts);
    setDate(dt);
  }, []);

  const dispatchShiftUpdate = useCallback(async (shift, isGroup, shiftToUpdate = null) => {
    const { emailNotification, pushNotification } = companySettings.settings;
    // Shift drop from sidebar does not set editShift
    const relevantShift = shiftToUpdate || editShift || shift;
    const day = relevantShift.day || (newShift?.day || shift.day);
    const dayKey = getDayKey(day);
    if (!(dayKey in shifts)) {
      shifts[dayKey] = [];
    }
    const {
      [dayKey]: dayShifts = [],
    } = shifts;

    const existingShift = dayShifts.find((dayShift) => dayShift.id === relevantShift.id);
    if (!existingShift) return false;
    const fullEditShift = {
      ...relevantShift,
      ...shift,
      emailNotification,
      pushNotification,
    };
    if (!shift.formTemplateId && existingShift.formTemplateId) {
      fullEditShift.formTemplateId = null;
    }
    const payload = prepareShiftUpdatePayload(existingShift, fullEditShift, isGroup);
    return dispatch(updateShift(relevantShift.id, payload));
  }, [editShift, shifts, newShift, companySettings]);

  const onShiftSave = useCallback(async (shift) => {
    const { emailNotification, pushNotification } = companySettings.settings;
    let passed = false;
    const { costcodeId } = shift;
    const splitCostcodeId = costcodeId?.split('.');
    const isSplit = splitCostcodeId?.length === 2;
    const ourCostcodeId = isSplit ? splitCostcodeId[1] : costcodeId;

    // Only update if editShift exists, or if selectedShift exists AND we're not creating
    const shiftToUpdate = editShift || (selectedShift && !newShift ? selectedShift : null);

    if (shiftToUpdate) {
      if (shiftToUpdate.groupId) {
        setChangeModalMode('update');
        setChangeModalType(shiftToUpdate.isEvent ? 'Event' : 'Shift');
        setStagedUpdate({
          ...shift,
          costcodeId: ourCostcodeId,
        });
        return;
      }
      passed = await dispatchShiftUpdate(
        { ...shift, costcodeId: ourCostcodeId },
        false,
        shiftToUpdate,
      );
    } else {
      const refinedReminders = refineReminders(shift.reminders);
      window.sessionStorage.setItem('lastReminderConfig', JSON.stringify(refinedReminders));
      const toCreate = {
        ...newShift,
        ...shift,
        reminders: refinedReminders,
        emailNotification,
        pushNotification,
        costcodeId: ourCostcodeId,
      };
      passed = await dispatch(createShift(toCreate));
    }
    if (passed) hideDrawer();
  }, [
    dispatch,
    newShift,
    editShift,
    selectedShift,
    hideDrawer,
    dispatchShiftUpdate,
    companySettings,
    shifts,
  ]);

  const onShiftApply = useCallback(async (shift, status) => {
    const userId = Permissions.id;

    dispatch(applyToShift(shift.id, { userId, status }));
  }, []);

  const onShiftSelect = useCallback((shift, setAsEdit = false) => {
    Analytics.track('Schedule/ShiftView');
    setNewShift();
    setSelectedShift(shift);
    setShowDrawer(true);
    setIsDisplay(defaultDisplay);
    if (setAsEdit) {
      setEditShift(shift);
    } else {
      setEditShift(undefined);
    }
  }, [defaultDisplay]);

  const onShiftEditAndSelect = useCallback((shift) => {
    setNewShift();
    setEditShift(shift);
    setSelectedShift(shift);
  }, []);

  const onShiftDelete = useCallback((shift) => {
    if (shift.groupId) {
      setChangeModalMode('delete');
      setChangeModalType(shift.isEvent ? 'Event' : 'Shift');
      setSelectedShift(shift);
    } else {
      showDeleteModal(shift, async () => {
        if (await dispatch(deleteShift(shift))) {
          hideDrawer();
        }
      });
    }
  }, [dispatch, hideDrawer]);

  const onShiftGroupSave = useCallback(async (mode) => {
    const isGroup = mode === 'all';
    if (changeModalMode === 'delete') {
      if (await dispatch(deleteShift(selectedShift, isGroup))) {
        hideDrawer();
      }
    } else if (await dispatchShiftUpdate(stagedUpdate, isGroup)) {
      hideDrawer();
    }
  }, [selectedShift, editShift, stagedUpdate, changeModalMode]);

  const onDaySelect = useCallback((day) => {
    Analytics.track('Schedule/ChangeView', { viewType: 'Day' });
    setDate(day);
    setViewType('Day');
    window.localStorage.setItem('scheduleViewType', 'Day');
  }, []);

  const onViewTypeChange = useCallback((newViewType) => {
    Analytics.track('Schedule/ChangeView', { viewType: newViewType });
    setViewType(newViewType);
    window.localStorage.setItem('scheduleViewType', newViewType);
  }, []);

  useEffect(() => {
    // Optimize this thing...
    if (dispatch) {
      dispatch(getUserLabels());
      dispatch(getCustomers());
      dispatch(getCustomerLabels());
      dispatch(getTemplates());
      dispatch(getEmails());
      dispatch(getBillingRates());
      dispatch(getStatuses());
      dispatch(getCustomTables());
    }
  }, [dispatch]);

  useEffect(() => {
    const currentEditShift = editShiftRef.current;
    if (!currentEditShift) return;
    const storedShift = shiftMap[currentEditShift.id];
    // Keep unsaved drag/resize times instead of reverting to the stored shift.
    const hasLocalTimeEdit = !!storedShift && (
      currentEditShift.startTime !== storedShift.startTime
      || currentEditShift.endTime !== storedShift.endTime
    );
    if (hasLocalTimeEdit) return;
    setSelectedShift(storedShift);
  }, [shiftMap]);

  useEffect(() => {
    const loadShifts = async ({ startTime, endTime }) => {
      setLoading(true);
      if (await dispatch(getSchedule({ startTime, endTime }))) {
        setLastDownload([startTime, endTime]);
      }
      setLoading(false);
    };
    if (showDrawer) return;
    let startDT = DateTime.local();
    let endDT = DateTime.local();

    switch (viewType) {
      case 'Month':
        startDT = date.startOf('month');
        endDT = date.endOf('month');
        break;
      case 'UserBiWeekly':
      case 'EquipmentBiWeekly':
        startDT = getWeekStart(date);
        endDT = getBiWeeklyEnd(date);
        break;
      case 'UserWeek':
      case 'EquipmentWeek':
        startDT = getWeekStart(date);
        endDT = getWeekEnd(date);
        break;
      case 'UserWeekdays':
      case 'EquipmentWeekdays':
        startDT = getWeekStart(date).plus({ days: 1 });
        endDT = getWeekEnd(date).minus({ days: 1 });
        break;
      default:
        startDT = getWeekStart(date);
        endDT = startDT.plus({ days: 7 });
        break;
    }
    const startTime = startDT.toMillis();
    const endTime = endDT.toMillis();
    const hasScheduleRestriction = !isNullOrUndefined(weeksRestriction);
    if (hasScheduleRestriction && getMaxStartFromRestriction(weeksRestriction) < startTime) return;
    if (!lastDownload || lastDownload[0] > startTime || lastDownload[1] < endTime) {
      loadShifts({ startTime, endTime });
    }
  }, [dispatch, lastDownload, date, viewType, showDrawer, weeksRestriction]);

  useEffect(() => {
    setShifts(stateShifts);
  }, [stateShifts]);

  useEffect(() => {
    setViewType(getViewTypeFromAsync());
  }, []);

  useEffect(() => {
    switch (viewType) {
      case 'UserBiWeekly':
      case 'EquipmentBiWeekly':
        setRangeStartDate(getWeekStart(date));
        setRangeEndDate(getBiWeeklyEnd(date));
        break;
      case 'UserWeek':
      case 'EquipmentWeek':
        setRangeStartDate(getWeekStart(date));
        setRangeEndDate(getWeekEnd(date));
        break;
      case 'UserWeekdays':
      case 'EquipmentWeekdays':
        setRangeStartDate(getWeekStart(date).plus({ days: 1 }));
        setRangeEndDate(getWeekEnd(date).minus({ days: 1 }));
        break;
      case 'UserDay':
      case 'EquipmentDay':
        setRangeStartDate(date.startOf('day'));
        setRangeEndDate(date.endOf('day'));
        break;
      // extend here for custom ranges
      default:
        break;
    }
  }, [date, viewType]);

  const userMap = useMemo(() => getIdMap(users.filter((user) => user.active)), [users]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const phaseMap = useMemo(() => getIdMap(phases), [phases]);
  const teamMap = useMemo(() => getIdMap(teams), [teams]);
  const divisionsList = useMemo(() => Object.values(divisions), [divisions]);
  const {
    Grid: ResourceAxisSchedule,
    List: ResourceAxisList,
  } = RESOURCE_AXIS_VIEWS[getScheduleAxis(viewType)] ?? {};

  const filteredShifts = useMemo(() => (
    filterShifts({
      shifts,
      day: date,
      searchText,
      userMap,
      viewType,
      rangeStartDate,
      rangeEndDate,
      filters,
      projectMap,
      equipment,
      equipmentTypes,
    })
  ), [
    searchText,
    shifts,
    userMap,
    date,
    viewType,
    rangeStartDate,
    rangeEndDate,
    filters,
    projectMap,
    equipment,
    equipmentTypes,
  ]);

  const disableHotkeys = useCallback(() => setCanUseHotkeys(false), []);
  const enableHotkeys = useCallback(() => setCanUseHotkeys(true), []);

  useEffect(() => {
    const hotkeys = (event) => {
      if (showDrawer || showFilterDrawer || !canUseHotkeys) return;
      const c = String.fromCharCode(event.keyCode);
      switch (c) {
        case 'D':
          onViewTypeChange('Day');
          break;
        case 'W':
          onViewTypeChange('Week');
          break;
        case 'M':
          onViewTypeChange('Month');
          break;
        case 'U':
          onViewTypeChange('UserDay');
          break;
        case 'B':
          onViewTypeChange('UserBiWeekly');
          break;
        case 'E':
          onViewTypeChange('EquipmentDay');
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', hotkeys, false);

    return () => {
      document.removeEventListener('keydown', hotkeys, false);
    };
  }, [showDrawer, canUseHotkeys, onViewTypeChange]);

  const onSidebarClose = useCallback(() => {
    window.localStorage.setItem(SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY, 'true');
    setIsSidebarOpen(false);
  }, []);

  const onSidebarOpen = useCallback(() => {
    window.localStorage.setItem(SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY, 'false');
    setIsSidebarOpen(true);
  }, []);

  const userAxisRange = useMemo(() => {
    switch (viewType) {
      case 'UserBiWeekly':
      case 'EquipmentBiWeekly':
        return { startDate: getWeekStart(date), endDate: getBiWeeklyEnd(date) };
      case 'UserWeek':
      case 'EquipmentWeek':
        return { startDate: getWeekStart(date), endDate: getWeekEnd(date) };
      case 'UserWeekdays':
      case 'EquipmentWeekdays':
        return {
          startDate: getWeekStart(date).plus({ days: 1 }),
          endDate: getWeekEnd(date).minus({ days: 1 }),
        };
      case 'UserDay':
      case 'EquipmentDay':
        return { startDate: date.startOf('day'), endDate: date.endOf('day') };
      default:
        return null;
    }
  }, [viewType, date]);

  const relevantShifts = useMemo(() => {
    switch (viewType) {
      case 'Day':
      case 'UserDay':
      case 'EquipmentDay':
        return filteredShifts[getDayKey(date)];
      case 'UserWeekdays':
      case 'EquipmentWeekdays':
      case 'UserWeek':
      case 'EquipmentWeek':
      case 'Week':
      case 'Month':
      case 'UserBiWeekly':
      case 'EquipmentBiWeekly':
        return filteredShifts;
      default:
        return filteredShifts;
    }
  }, [viewType, filteredShifts, date]);

  const onExport = useCallback(() => {
    const { startDate, endDate } = getViewDateRange(date, viewType);
    exportFieldScheduleToExcel({
      shifts: relevantShifts,
      startDate,
      endDate,
      userMap,
      projectMap,
      phaseMap,
      costcodeIdMap,
      divisionMap: divisions,
      teamMap,
    });
  }, [
    relevantShifts,
    date,
    viewType,
    userMap,
    projectMap,
    phaseMap,
    costcodeIdMap,
    divisions,
    teamMap,
  ]);

  const onShiftDrop = useCallback(async ({
    day,
    shift,
    shiftField,
    resourceId,
  } = {}) => {
    const {
      startTime,
      endTime,
      [shiftField]: shiftResourceIds = [],
    } = shift || {};

    if (!startTime || !endTime) return;

    let startTimeDT = DateTime.fromMillis(startTime);
    let endTimeDT = DateTime.fromMillis(endTime);

    const daySpan = Math.round(
      endTimeDT.startOf('day').diff(startTimeDT.startOf('day'), 'days').days,
    );

    startTimeDT = startTimeDT.set({
      year: day.year,
      month: day.month,
      day: day.day,
    });

    const adjustedDay = day.plus({ days: daySpan });

    endTimeDT = endTimeDT.set({
      year: adjustedDay.year,
      month: adjustedDay.month,
      day: adjustedDay.day,
    });

    const payload = {
      ...shift,
      startTime: startTimeDT.toMillis(),
      endTime: endTimeDT.toMillis(),
      categoryId: shift.shiftCategoryId,
    };
    if (resourceId && !shiftResourceIds.includes(resourceId)) {
      payload[shiftField] = [...shiftResourceIds, resourceId];
    }

    await dispatchShiftUpdate(payload);
  }, [dispatchShiftUpdate]);

  const shiftArray = useMemo(() => Object.values(shifts).flat(), [shifts]);

  useUpdateCostcodeIdMap({
    items: shiftArray,
  });

  if (!Permissions.match('SCHEDULE')) {
    history.replace('/dashboard');
    return null;
  }

  return (
    <BreadCrumbContainer
      crumbs={crumbs}
      bodyStyle={{
        position: 'absolute',
        top: 71,
        left: 0,
        right: 0,
        bottom: 0,
        height: 'auto',
        margin: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
      redesignBodyStyle={{
        marginTop: 0,
      }}
      crumbStyle={{
        margin: '10px 0px',
      }}
    >
      <Row style={{ flex: 1, minHeight: 0, width: '100%', flexDirection: 'column', flexWrap: 'nowrap', overflow: 'hidden' }}>
        <ScheduleHeader
          viewType={viewType}
          onViewTypeChange={onViewTypeChange}
          onDateChange={onDateChange}
          date={date}
          onSearch={setSearchText}
          onAdd={openDrawer}
          onExport={onExport}
          enableHotkeys={enableHotkeys}
          disableHotkeys={disableHotkeys}
          openFilterDrawer={openFilterDrawer}
          filters={filters}
          filterViews={filterViews}
          isSideBarOpen={isSidebarOpen}
          layout={layout}
          onLayoutChange={setLayout}
        />
        <DndProvider backend={HTML5Backend}>
        <Row style={{ flex: 1, minHeight: 0, width: '100%', flexWrap: 'nowrap', overflow: 'hidden' }}>
          <Col flex="none" style={{ height: '100%' }}>
            <UnassignedScheduleSidebar
              open={isSidebarOpen}
              shifts={relevantShifts}
              onSidebarClose={onSidebarClose}
              onSidebarOpen={onSidebarOpen}
              onShiftSelect={onShiftSelect}
            />
          </Col>
          <Col flex="auto" style={{ height: '100%', minWidth: 0 }}>
            <Row id="schedule-container" style={{ paddingBottom: 10, height: '100%', minHeight: 0 }}>
              {layout === 'list' && !userAxisRange && (
                <ScheduleDayList
                  date={date}
                  viewType={viewType}
                  shifts={filteredShifts}
                  onShiftSelect={onShiftSelect}
                />
              )}
              {layout === 'list' && userAxisRange && ResourceAxisList && (
                <ResourceAxisList
                  startDate={userAxisRange.startDate}
                  endDate={userAxisRange.endDate}
                  shifts={filteredShifts}
                  onShiftSelect={onShiftSelect}
                />
              )}
              {layout !== 'list' && viewType === 'Day' && (
              <DailySchedule
                day={date}
                onShiftCreate={onShiftCreate}
                shifts={filteredShifts[getDayKey(date)]}
                newShift={newShift}
                onNewShiftChange={setNewShift}
                onShiftSelect={onShiftSelect}
                editShift={editShift}
                onShiftEdit={onShiftEditAndSelect}
              />
              )}
              {layout !== 'list' && viewType === 'Week' && (
              <WeeklySchedule
                date={date}
                onShiftCreate={onShiftCreate}
                shifts={filteredShifts}
                newShift={newShift}
                onNewShiftChange={setNewShift}
                onShiftSelect={onShiftSelect}
                editShift={editShift}
                onShiftEdit={onShiftEditAndSelect}
                onDaySelect={onDaySelect}
              />
              )}

              {layout !== 'list' && viewType === 'Month'
                && (
                <MonthlySchedule
                  date={date}
                  onDaySelect={onDaySelect}
                  shifts={filteredShifts}
                  onShiftSelect={onShiftSelect}
                  onShiftEdit={onShiftEditAndSelect}
                />
                )}

              {layout !== 'list' && userAxisRange && ResourceAxisSchedule && (
                <ResourceAxisSchedule
                  startDate={userAxisRange.startDate}
                  endDate={userAxisRange.endDate}
                  filters={filters}
                  searchText={searchText}
                  shifts={filteredShifts}
                  onShiftSelect={onShiftSelect}
                  onShiftEdit={onShiftEditAndSelect}
                  editShift={editShift}
                  onShiftCreate={onShiftCreate}
                  newShift={newShift}
                  onNewShiftChange={setNewShift}
                  onDaySelect={onDaySelect}
                  viewType={viewType}
                  onShiftDrop={onShiftDrop}
                />
              )}
              {
                loading
                && (
                <Row className="schedule-loading-container" justify="center" align="middle">
                  <Col>
                    <Spin />
                  </Col>
                </Row>
                )
              }
            </Row>
          </Col>
        </Row>
        </DndProvider>
      </Row>
      <ScheduleDrawer
        width="calc(100vw - 200px)"
        shift={selectedShift || editShift || newShift}
        visible={showDrawer}
        onClose={hideDrawer}
        onSave={onShiftSave}
        onDelete={onShiftDelete}
        isNew={newShift}
        isDisplay={isDisplay}
        openFormDetail={openFormDetail}
        shifts={shifts}
        shiftMap={shiftMap}
        lastDownload={lastDownload}
        setLastDownload={setLastDownload}
        onApply={onShiftApply}
      />
      <RecurringChangeModal
        mode={changeModalMode}
        onClose={hideChangeModal}
        onSave={onShiftGroupSave}
        type={changeModalType}
      />
      <FormDetailView
        visible={showFormDetail}
        onClose={closeFormDetail}
        userMap={userMap}
        shouldRedirect={false}
      />
      <FilterDrawer
        visible={showFilterDrawer}
        onClose={closeFilterDrawer}
        initialTab={filterDrawerTab}
        filterViews={filterViews}
        CustomFilterTab={FieldScheduleCustomFilterTab}
        users={users}
        projects={projects}
        phases={phases}
        divisions={divisionsList}
        teams={teams}
        formTemplates={formTemplates}
      />
    </BreadCrumbContainer>
  );
}

Schedule.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
};

Schedule.defaultProps = {
  history: {},
};
